import React, { useCallback, useState } from 'react'
import { ApolloClient } from '@apollo/client'
import styled, { css, keyframes } from 'styled-components'
import { TowingOrderRow } from '../../../components/TowingOrderRow'
import { TowingRecordRow } from '../../../components/TowingRecordRow'
import { RefreshIndicator } from '../../../components/PullToRefresh/PullToRefreshIndicator'
import { NoTowingJobs } from '../NoTowingJobs'
import { getStationByIdFromSettings } from '../../Parameters/station'
import { getOperatorLabelFromId } from '../../Parameters/user'
import { RepairShop } from '../../TowingRecordPage/components/routes/subforms/FinishRoute/options'
import { User } from '../../../common/types'
import { Settings } from '../../Parameters/types'
import { TowingJobsResponse } from '../TowingJobsQuery'
import { useTowingJobsListChange } from '../towingJobsWatcher'
import { messages } from '../messages'

export interface TowingJobsListProps {
  data: TowingJobsResponse | undefined
  loading: boolean
  refetch: () => Promise<ApolloClient.QueryResult<TowingJobsResponse>>
  currentUser?: User
  settings?: Settings
  repairShops: RepairShop[]
  translateMessage: (id: string) => string
  skip?: boolean
  expandedIds: Set<number>
  onExpandRow: (id: number) => void
  isInitialLoad: boolean
}

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`

const skeletonBase = css`
  background: linear-gradient(90deg, #ebebeb 25%, #f5f5f5 50%, #ebebeb 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
  border-radius: 3px;
`

const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: 4fr 120px 4fr 1fr;
  padding: 1rem 1.5rem;
  border-top: 1px solid #d6dadc;
  border-bottom: 1px solid #d6dadc;
  margin-bottom: -1px;
  background: ${props => (props.color ? props.theme.colors[props.color] : props.theme.colors.white)};
  align-items: center;

  @media (max-width: 600px) {
    grid-template-columns: 9fr 1fr;
    grid-template-rows: 1fr auto;
    gap: 0.5rem;
  }
`

const SkeletonCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`

const SkeletonBlock = styled.div<{ width?: string; height?: string }>`
  ${skeletonBase}
  width: ${({ width }) => width ?? '80%'};
  height: ${({ height }) => height ?? '14px'};
`

const SkeletonLogoBlock = styled.div`
  ${skeletonBase}
  width: 55px;
  height: 22px;
  margin: 0 auto;

  @media (max-width: 600px) {
    display: none;
  }
`

const SkeletonArrow = styled.div`
  ${skeletonBase}
  width: 16px;
  height: 16px;
  border-radius: 50%;
  margin-left: auto;
`

const TowingJobsList: React.FunctionComponent<TowingJobsListProps> = ({
  data,
  loading,
  refetch,
  currentUser,
  settings,
  repairShops,
  translateMessage,
  skip,
  expandedIds,
  onExpandRow,
  isInitialLoad,
}) => {
  const [updateAvailable, setUpdateAvailable] = useState(false)

  const callback = useCallback(() => {
    // timeout because backend sends socket notification before new order is available via REST
    setTimeout(() => setUpdateAvailable(true), 5000)
  }, [])

  useTowingJobsListChange(callback)

  const noTowingJobs =
    !!data &&
    (data?.towingOrders?.towingOrders?.length ?? 0) === 0 &&
    (data?.towingRecords?.towingRecords?.length ?? 0) === 0

  const isBackgroundRefresh = loading && !isInitialLoad
  const isAwaitingFilters = !!skip && !data
  const showSkeleton = skip || (loading && !data)

  return (
    <>
      <RefreshIndicator
        label={updateAvailable ? translateMessage(messages.refreshUpdate.id) : translateMessage(messages.refreshUpdating.id)}
        onClick={async () => {
          await refetch();
          setUpdateAvailable(false)
        }}
        visible={updateAvailable || isBackgroundRefresh || isAwaitingFilters}
        isRefreshing={isBackgroundRefresh || isAwaitingFilters}
      />
      {showSkeleton ? (
        Array.from({ length: 5 }, (_, i) => (
          <SkeletonRow key={i}>
            <SkeletonCell>
              <SkeletonBlock width="55%" height="20px" />
              <SkeletonBlock width="70%" height="13px" />
              <SkeletonBlock width="45%" height="13px" />
            </SkeletonCell>
            <SkeletonLogoBlock />
            <SkeletonCell>
              <SkeletonBlock width="40%" height="13px" />
              <SkeletonBlock width="65%" height="13px" />
            </SkeletonCell>
            <SkeletonArrow />
          </SkeletonRow>
        ))
      ) : (
        <>
          {noTowingJobs && <NoTowingJobs />}
          {data?.towingOrders?.towingOrders?.map(towingOrder => (
            <TowingOrderRow
              repairShops={repairShops}
              expandedRow={expandedIds.has(towingOrder.id)}
              handleItemClick={onExpandRow}
              towingOrder={towingOrder}
              translateMessage={translateMessage}
              key={towingOrder.id}
              slug={`/towing-order/${towingOrder.id}`}
              station={getStationByIdFromSettings(towingOrder.stationId, settings!)}
              operatorName={getOperatorLabelFromId(
                towingOrder.operatorId,
                currentUser!,
                settings!.operators,
                translateMessage,
              )}
            />
          ))}
          {data?.towingRecords?.towingRecords?.map(towingRecord => (
            <TowingRecordRow
              repairShops={repairShops}
              expandedRow={expandedIds.has(towingRecord.id)}
              handleItemClick={onExpandRow}
              towingRecord={towingRecord}
              translateMessage={translateMessage}
              key={towingRecord.id}
              slug={`/towing-record/${towingRecord.id}`}
              station={getStationByIdFromSettings(towingRecord.stationId, settings!)}
              operatorName={getOperatorLabelFromId(
                towingRecord.operatorId,
                currentUser!,
                settings!.operators,
                translateMessage,
              )}
            />
          ))}
        </>
      )}
    </>
  )
}

export default TowingJobsList
